import { useEffect, useRef, useState } from "react";
import { fmtMYT } from "@/lib/myt";
import { Bot, ImagePlus, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const shortId = (id) => (id || "").slice(-6);

const EMPTY_EA = {
  name: "", short_description: "", full_description: "", claim_requirements: "",
  all_brokers: true, allowed_brokers: [], cover_image_url: "", status: "active",
  external_link: "", installation_guide: "", access_type: "free", eligible_plans: [], coin_price: "",
};

const STATUS_LABELS = {
  ib_review: "Pending IB Approval",
  pending_admin: "Pending Admin Approval",
  ib_rejected: "Rejected by IB",
  admin_rejected: "Rejected by Admin",
  approved: "Approved",
};

function EAForm({ value, onChange, brokers, idPrefix }) {
  const [customBroker, setCustomBroker] = useState("");
  const toggleBroker = (b) => {
    const has = value.allowed_brokers.includes(b);
    onChange({ ...value, allowed_brokers: has ? value.allowed_brokers.filter((x) => x !== b) : [...value.allowed_brokers, b] });
  };
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">EA Name</label>
        <input className="input-lux" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder="e.g. GOLD MASTER EA" data-testid={`${idPrefix}-name-input`} />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Short Description</label>
        <input className="input-lux" value={value.short_description} onChange={(e) => onChange({ ...value, short_description: e.target.value })} placeholder="One-line summary" data-testid={`${idPrefix}-short-input`} />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Full Description</label>
        <textarea className="input-lux min-h-[90px]" value={value.full_description} onChange={(e) => onChange({ ...value, full_description: e.target.value })} data-testid={`${idPrefix}-full-input`} />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Claim Requirements</label>
        <textarea className="input-lux min-h-[60px]" value={value.claim_requirements} onChange={(e) => onChange({ ...value, claim_requirements: e.target.value })} placeholder="e.g. Minimum rank, account size…" data-testid={`${idPrefix}-requirements-input`} />
      </div>
      <div>
        <label className="flex items-center gap-2.5 text-xs font-medium text-[#94A3B8] tracking-wide cursor-pointer">
          <input
            type="checkbox"
            checked={value.all_brokers}
            onChange={(e) => onChange({ ...value, all_brokers: e.target.checked, allowed_brokers: e.target.checked ? [] : value.allowed_brokers })}
            className="accent-[#D4AF37] h-4 w-4"
            data-testid={`${idPrefix}-broker-all`}
          />
          Available for all brokers
        </label>
        {!value.all_brokers && (
          <div className="mt-3 space-y-2" data-testid={`${idPrefix}-broker-picker`}>
            <div className="flex flex-wrap gap-2">
              {brokers.map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => toggleBroker(b)}
                  className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border transition-colors ${value.allowed_brokers.includes(b) ? "text-black bg-[#D4AF37] border-[#D4AF37]" : "text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"}`}
                  data-testid={`${idPrefix}-broker-option-${b}`}
                >
                  {b}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="input-lux flex-1 !py-2 text-xs" value={customBroker} onChange={(e) => setCustomBroker(e.target.value.toUpperCase())} placeholder="Add broker (e.g. ULTIMA)" data-testid={`${idPrefix}-broker-custom-input`} />
              <button
                type="button"
                onClick={() => {
                  const b = customBroker.trim().toUpperCase();
                  if (b && !value.allowed_brokers.includes(b)) onChange({ ...value, allowed_brokers: [...value.allowed_brokers, b] });
                  setCustomBroker("");
                }}
                className="btn-gold-outline px-4 py-2 text-xs"
                data-testid={`${idPrefix}-broker-add-button`}
              >
                Add
              </button>
            </div>
            {value.allowed_brokers.length > 0 && (
              <p className="text-[10px] text-[#64748B] font-mono">Restricted to: {value.allowed_brokers.join(", ")}</p>
            )}
          </div>
        )}
      </div>
      <div className="border border-[#222530] rounded-lg p-4 space-y-3" data-testid={`${idPrefix}-access-config`}>
        <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Access Configuration</p>
        <div className="flex gap-2">
          {[["free", "FREE"], ["coin", "COINS"]].map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange({ ...value, access_type: v })}
              className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border transition-colors ${value.access_type === v ? "text-black bg-[#D4AF37] border-[#D4AF37]" : "text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"}`}
              data-testid={`${idPrefix}-access-${v}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="border border-[#222530] rounded-lg p-4">
        <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-2">
          {value.access_type === "coin" ? "Coins Purchase (required — 1 COINS = USD 1)" : "Coins Purchase (optional — 1 COINS = USD 1)"}
        </p>
        <label className="block text-[10px] font-medium text-[#94A3B8] mb-1 tracking-wide">Price (COINS){value.access_type === "coin" ? " *" : " — empty = not purchasable"}</label>
        <input type="number" min="0" step="0.01" className="input-lux !py-2 text-xs font-mono" value={value.coin_price ?? ""} onChange={(e) => onChange({ ...value, coin_price: e.target.value })} placeholder="e.g. 250" data-testid={`${idPrefix}-coin-price-input`} />
        <p className="text-[10px] text-[#64748B] mt-2">EA purchase = permanent ownership. No expiry, no auto-renewal. Owned EAs cannot be purchased again.</p>
      </div>
      <div>
        <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">External Access Link <span className="text-[#64748B]">(optional)</span></label>
        <input className="input-lux font-mono" value={value.external_link} onChange={(e) => onChange({ ...value, external_link: e.target.value })} placeholder="https://…" data-testid={`${idPrefix}-link-input`} />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Installation Guide <span className="text-[#64748B]">(optional — line breaks supported)</span></label>
        <textarea className="input-lux min-h-[90px]" value={value.installation_guide} onChange={(e) => onChange({ ...value, installation_guide: e.target.value })} placeholder={"1. Open MetaTrader…\n2. Copy the file to…"} data-testid={`${idPrefix}-guide-input`} />
      </div>
    </div>
  );
}

export default function AdminEA() {
  const [tab, setTab] = useState("manage");
  const [eas, setEas] = useState([]);
  const [claims, setClaims] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [form, setForm] = useState(EMPTY_EA);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editEA, setEditEA] = useState(null);
  const [editError, setEditError] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const createCoverRef = useRef(null);
  const editCoverRef = useRef(null);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/expert-advisors").then((res) => setEas(res.data)).catch(() => {});
    api.get("/admin/ea-claims").then((res) => setClaims(res.data)).catch(() => {});
    api.get("/admin/brokers").then((res) => setBrokers(res.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const uploadCover = async (file, apply) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/uploads/image", fd);
      apply(res.data.url);
      toast.success("Cover image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const payloadOf = (v) => ({
    name: v.name, short_description: v.short_description, full_description: v.full_description,
    claim_requirements: v.claim_requirements, allowed_brokers: v.all_brokers ? [] : v.allowed_brokers,
    cover_image_url: v.cover_image_url || null, status: v.status,
    external_link: v.external_link || null, installation_guide: v.installation_guide,
    access_type: v.access_type || "free", eligible_plans: v.eligible_plans || [],
    coin_price_minor: v.coin_price !== "" && v.coin_price != null ? Math.round(Number(v.coin_price) * 100) : (v.coin_price_minor ?? null),
  });

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    if (form.access_type === "coin" && !(Number(form.coin_price) > 0)) {
      setError("Coins price is required when access type is Coins");
      setBusy(false);
      return;
    }
    try {
      await api.post("/admin/expert-advisors", payloadOf(form));
      toast.success("Expert Advisor created");
      setForm(EMPTY_EA);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
    setBusy(false);
  };

  const saveEdit = async () => {
    setBusy(true);
    setEditError("");
    if (editEA.access_type === "coin" && !(Number(editEA.coin_price) > 0)) {
      setEditError("Coins price is required when access type is Coins");
      setBusy(false);
      return;
    }
    try {
      await api.put(`/admin/expert-advisors/${editEA.id}`, payloadOf(editEA));
      toast.success("Expert Advisor updated");
      setEditEA(null);
      load();
    } catch (err) {
      setEditError(formatApiError(err));
    }
    setBusy(false);
  };

  const toggle = async (ea) => {
    try {
      await api.put(`/admin/expert-advisors/${ea.id}`, payloadOf({
        ...ea, all_brokers: (ea.allowed_brokers || []).length === 0,
        status: ea.status === "active" ? "inactive" : "active",
      }));
      toast.success(ea.status === "active" ? "Deactivated" : "Activated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (ea) => {
    if (!window.confirm(`Delete "${ea.name}"? Claim history is preserved.`)) return;
    try {
      await api.delete(`/admin/expert-advisors/${ea.id}`);
      toast.success("Expert Advisor deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadFile = async (file) => {
    if (!file || !editEA) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/admin/expert-advisors/${editEA.id}/files`, fd);
      setEditEA((m) => ({ ...m, delivery_files: res.data.delivery_files }));
      toast.success("Delivery file uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteFile = async (index) => {
    try {
      const res = await api.delete(`/admin/expert-advisors/${editEA.id}/files/${index}`);
      setEditEA((m) => ({ ...m, delivery_files: res.data.delivery_files }));
      toast.success("File removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const approveClaim = async (cl) => {
    try {
      await api.post(`/admin/ea-claims/${cl.id}/approve`);
      toast.success("Claim approved — client now has EA access");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitReject = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/ea-claims/${rejectTarget.id}/reject`, { reason: rejectReason });
      toast.success("Claim rejected");
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="admin-ea-page">
      <div>
        <p className="eyebrow-label">Administration</p>
        <h1 className="page-title mt-2">Expert Advisors</h1>
        <p className="page-sub mt-1">Manage the EA catalog, delivery content, and the client → IB → admin claim pipeline.</p>
      </div>

      <div className="flex gap-2" data-testid="ea-admin-tabs">
        <button onClick={() => setTab("manage")} className={`px-4 py-2 text-xs rounded-lg border transition-colors ${tab === "manage" ? "bg-[#D4AF37] text-black border-[#D4AF37] font-semibold" : "text-[#94A3B8] border-[#222530] hover:text-white"}`} data-testid="ea-manage-tab-button">
          Manage EAs
        </button>
        <button onClick={() => setTab("claims")} className={`px-4 py-2 text-xs rounded-lg border transition-colors ${tab === "claims" ? "bg-[#D4AF37] text-black border-[#D4AF37] font-semibold" : "text-[#94A3B8] border-[#222530] hover:text-white"}`} data-testid="ea-claims-tab-button">
          EA Claims
        </button>
      </div>

      {tab === "manage" && (
        <>
          <form onSubmit={create} className="card-lux p-6 space-y-4" data-testid="ea-create-form">
            <p className="text-sm font-semibold text-white flex items-center gap-2"><Bot className="h-4 w-4 text-[#D4AF37]" /> New Expert Advisor</p>
            <EAForm value={form} onChange={setForm} brokers={brokers} idPrefix="ea" />
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Cover Image <span className="text-[#64748B]">(optional)</span></label>
              <input ref={createCoverRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { uploadCover(e.target.files?.[0], (url) => setForm((f) => ({ ...f, cover_image_url: url }))); e.target.value = ""; }} data-testid="ea-cover-input" />
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => createCoverRef.current?.click()} className="btn-gold-outline px-4 py-2 text-xs flex items-center gap-2" data-testid="ea-cover-upload">
                  <ImagePlus className="h-3.5 w-3.5" /> Upload Cover
                </button>
                {form.cover_image_url && <img src={`${API}${form.cover_image_url}`} alt="Cover" className="h-12 w-12 object-cover rounded-md border border-[#D4AF37]/40" data-testid="ea-cover-preview" />}
              </div>
            </div>
            {error && <p className="text-xs text-red-400" data-testid="ea-create-error">{error}</p>}
            <button type="submit" disabled={busy} className="btn-gold px-6 py-2.5 text-xs disabled:opacity-50" data-testid="ea-create-button">
              {busy ? "Saving…" : "Create Expert Advisor"}
            </button>
          </form>

          <div className="space-y-3" data-testid="ea-admin-list">
            {eas.length === 0 ? (
              <p className="text-[#64748B] text-sm" data-testid="ea-admin-empty">No Expert Advisors yet.</p>
            ) : (
              eas.map((ea) => (
                <div key={ea.id} className="card-lux p-5 flex items-center gap-4" data-testid={`ea-item-${shortId(ea.id)}`}>
                  {ea.cover_image_url ? (
                    <img src={`${API}${ea.cover_image_url}`} alt="" className="h-12 w-12 object-cover rounded-md border border-[#222530] shrink-0" />
                  ) : (
                    <div className="h-12 w-12 rounded-md border border-[#222530] bg-[#0B0C10] flex items-center justify-center shrink-0">
                      <Bot className="h-5 w-5 text-[#D4AF37]/50" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{ea.name}</p>
                    <p className="text-[11px] text-[#64748B] font-mono mt-0.5">
                      {ea.allowed_brokers?.length ? `Brokers: ${ea.allowed_brokers.join(", ")}` : "All brokers"} · {ea.delivery_files?.length || 0} file(s) ·{" "}
                      {ea.access_type === "coin" ? (
                        <span className="text-[#D4AF37]" data-testid={`ea-access-type-${shortId(ea.id)}`}>COINS{ea.coin_price_minor != null ? ` ${(ea.coin_price_minor / 100).toLocaleString("en-US")}` : ""}</span>
                      ) : (
                        <span className="text-emerald-400" data-testid={`ea-access-type-${shortId(ea.id)}`}>FREE</span>
                      )}
                    </p>
                  </div>
                  <span className={`text-[10px] font-mono tracking-widest uppercase px-2 py-0.5 rounded-full border ${ea.status === "active" ? "text-emerald-400 border-emerald-500/30" : "text-[#94A3B8] border-[#222530]"}`} data-testid={`ea-status-${shortId(ea.id)}`}>
                    {ea.status}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggle(ea)} className="btn-gold-outline px-3.5 py-1.5 text-[11px]" data-testid={`ea-toggle-${shortId(ea.id)}`}>
                      {ea.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                    <button onClick={() => { setEditError(""); setEditEA({ ...ea, all_brokers: (ea.allowed_brokers || []).length === 0, allowed_brokers: ea.allowed_brokers || [], external_link: ea.external_link || "", installation_guide: ea.installation_guide || "", cover_image_url: ea.cover_image_url || "", coin_price: ea.coin_price_minor != null ? String(ea.coin_price_minor / 100) : "" }); }} className="p-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors" data-testid={`ea-edit-${shortId(ea.id)}`} aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(ea)} className="p-2 text-[#94A3B8] hover:text-red-400 transition-colors" data-testid={`ea-delete-${shortId(ea.id)}`} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {tab === "claims" && (
        <div className="card-lux overflow-hidden" data-testid="ea-claims-admin-section">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ea-claims-admin-table">
              <thead>
                <tr className="border-b border-[#222530] text-left">
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Client</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Expert Advisor</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">IB</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden lg:table-cell">Timeline</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#222530]">
                {claims.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-[#64748B]" data-testid="ea-claims-admin-empty">No EA claims yet.</td></tr>
                ) : (
                  claims.map((cl) => (
                    <tr key={cl.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`ea-claim-row-${shortId(cl.id)}`}>
                      <td className="px-5 py-4">
                        <p className="text-white font-medium">{cl.member_name}</p>
                        <p className="text-[10px] text-[#64748B] font-mono">{cl.member_uid} · {cl.member_broker || "—"} · {cl.member_rank || "—"}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-white">{cl.ea_name}</p>
                        {cl.ea_requirements && <p className="text-[10px] text-[#64748B] mt-0.5 max-w-[200px] truncate" title={cl.ea_requirements}>Requires: {cl.ea_requirements}</p>}
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <p className="text-[#94A3B8] text-xs">{cl.ib_name || "—"}</p>
                        <p className="text-[10px] text-[#64748B] font-mono">{cl.ib_uid || ""}</p>
                      </td>
                      <td className="px-5 py-4 hidden lg:table-cell text-[11px] text-[#64748B] font-mono">
                        <p>Requested: {cl.created_at ? fmtMYT(cl.created_at) : "—"}</p>
                        {cl.ib_decision_at && <p>IB: {fmtMYT(cl.ib_decision_at)}</p>}
                        {cl.admin_decision_at && <p>Admin: {fmtMYT(cl.admin_decision_at)}</p>}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                          cl.status === "approved" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30"
                          : cl.status === "ib_rejected" || cl.status === "admin_rejected" ? "text-red-400 bg-red-400/10 border-red-400/30"
                          : "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30"
                        }`} data-testid={`ea-claim-status-${shortId(cl.id)}`}>
                          {STATUS_LABELS[cl.status] || cl.status}
                        </span>
                        {cl.subscription_plan && (
                          <p className="text-[9px] font-mono tracking-widest uppercase text-[#D4AF37] mt-1" data-testid={`ea-claim-plan-${shortId(cl.id)}`}>Subscription · {cl.subscription_plan}</p>
                        )}
                        {cl.approval_mode === "auto" && (
                          <p className="text-[9px] font-mono tracking-widest uppercase text-emerald-400 mt-1" data-testid={`ea-claim-auto-${shortId(cl.id)}`}>AUTO APPROVED</p>
                        )}
                        {cl.status === "approved" && cl.access_status === "paused" && (
                          <p className="text-[10px] text-amber-400 mt-1" data-testid={`ea-claim-paused-${shortId(cl.id)}`}>ACCESS PAUSED — subscription expired</p>
                        )}
                        {(cl.ib_remark || cl.admin_reason) && (
                          <p className="text-[10px] text-[#64748B] mt-1 max-w-[180px] truncate" title={cl.ib_remark || cl.admin_reason}>{cl.ib_remark || cl.admin_reason}</p>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {cl.status === "pending_admin" && (
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => approveClaim(cl)} className="text-xs text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`ea-claim-approve-${shortId(cl.id)}`}>
                              Final Approve
                            </button>
                            <button onClick={() => { setRejectReason(""); setRejectTarget(cl); }} className="text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`ea-claim-reject-${shortId(cl.id)}`}>
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editEA && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setEditEA(null)} />
          <div className="relative card-lux w-full max-w-xl max-h-[85vh] overflow-y-auto p-6 space-y-4" data-testid="ea-edit-modal">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white">Edit Expert Advisor</p>
              <button onClick={() => setEditEA(null)} className="text-[#94A3B8] hover:text-white p-1" data-testid="ea-edit-cancel-button" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <EAForm value={editEA} onChange={setEditEA} brokers={brokers} idPrefix="ea-edit" />
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Cover Image</label>
              <input ref={editCoverRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { uploadCover(e.target.files?.[0], (url) => setEditEA((m) => ({ ...m, cover_image_url: url }))); e.target.value = ""; }} data-testid="ea-edit-cover-input" />
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => editCoverRef.current?.click()} className="btn-gold-outline px-4 py-2 text-xs flex items-center gap-2" data-testid="ea-edit-cover-upload">
                  <ImagePlus className="h-3.5 w-3.5" /> Upload Cover
                </button>
                {editEA.cover_image_url && <img src={`${API}${editEA.cover_image_url}`} alt="Cover" className="h-12 w-12 object-cover rounded-md border border-[#D4AF37]/40" />}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Delivery Files <span className="text-[#64748B]">(EX4, EX5, MQ4, MQ5, ZIP, PDF, TXT — private)</span></label>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="ea-file-input" />
              <div className="space-y-2">
                {(editEA.delivery_files || []).map((f) => (
                  <div key={f.index} className="flex items-center justify-between rounded-lg border border-[#222530] px-3.5 py-2" data-testid={`ea-file-row-${f.index}`}>
                    <p className="text-xs text-[#94A3B8] font-mono">{f.name}</p>
                    <button type="button" onClick={() => deleteFile(f.index)} className="text-[#64748B] hover:text-red-400 p-1" data-testid={`ea-file-delete-${f.index}`} aria-label="Remove file">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => fileRef.current?.click()} className="btn-gold-outline px-4 py-2 text-xs flex items-center gap-2" data-testid="ea-file-upload-button">
                  <Upload className="h-3.5 w-3.5" /> Upload Delivery File
                </button>
              </div>
            </div>
            {editError && <p className="text-xs text-red-400" data-testid="ea-edit-error">{editError}</p>}
            <button onClick={saveEdit} disabled={busy} className="btn-gold px-6 py-2.5 text-xs w-full disabled:opacity-50" data-testid="ea-edit-save-button">
              {busy ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setRejectTarget(null)} />
          <div className="relative card-lux w-full max-w-md p-6 sm:p-8 space-y-4" data-testid="ea-admin-reject-modal">
            <p className="eyebrow-label">Reject EA Claim</p>
            <h3 className="font-heading text-lg font-semibold text-white">{rejectTarget.ea_name} — {rejectTarget.member_name}</h3>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Rejection Reason <span className="text-red-400">*</span></label>
              <textarea className="input-lux min-h-[80px]" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="ea-admin-reject-reason-input" />
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={submitReject} disabled={busy || rejectReason.trim().length < 2} className="flex-1 py-2.5 text-sm rounded-lg font-medium bg-red-500/90 hover:bg-red-500 text-white transition-colors disabled:opacity-50" data-testid="ea-admin-reject-confirm-button">
                {busy ? "Submitting…" : "Confirm Rejection"}
              </button>
              <button onClick={() => setRejectTarget(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="ea-admin-reject-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
